import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Calendar, MapPin, Trophy, ArrowRight, Flame } from "lucide-react";

/**
 * TRIBUN — Fan-focused, image-heavy, intense visuals.
 * Hero: fullscreen photo, bold typography overlay
 * Sections: massive imagery, social-media style
 */
export default function TribunTemplate() {
    const { branding } = useBranding();
    const [data, setData] = useState({ slides: [], next: null, last: null, players: [], posts: [], standings: [] });

    useEffect(() => {
        Promise.all([
            publicApi.heroSlides().catch(() => []),
            publicApi.nextMatch().catch(() => null),
            publicApi.lastMatch().catch(() => null),
            publicApi.players().catch(() => []),
            publicApi.posts({ limit: 4 }).catch(() => []),
            publicApi.standings().catch(() => []),
        ]).then(([slides, next, last, players, posts, standings]) =>
            setData({ slides, next, last, players, posts, standings })
        );
    }, []);

    const heroImg = data.slides[0]?.image_url || branding.logo_url;
    const primary = branding.primary_color || "#f5dc4c";

    return (
        <PublicLayout>
            <div data-testid="template-tribun" className="bg-black text-white">
                {/* Fullscreen Hero */}
                <section className="relative min-h-[90vh] flex items-end overflow-hidden">
                    {heroImg && <img src={heroImg} alt={branding.name} className="absolute inset-0 w-full h-full object-cover" />}
                    <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,1) 100%)` }} />
                    <div className="relative z-10 container-x pb-20 max-w-7xl mx-auto px-6">
                        <div className="overline mb-4 flex items-center gap-2" style={{ color: primary }}>
                            <Flame size={16}/> TRİBÜN | TARAFTAR PORTALI
                        </div>
                        <h1 className="text-6xl md:text-8xl font-extrabold tracking-tighter uppercase leading-none mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                            {branding.name}
                        </h1>
                        <p className="text-2xl text-neutral-300 max-w-2xl mb-8">{branding.description || "Taraftarın gücü, kulübün ruhu"}</p>
                        <div className="flex gap-4">
                            <Link to="/mac-merkezi" className="px-8 py-4 font-bold uppercase tracking-widest transition-all hover:scale-105" style={{ background: primary, color: "#000" }}>
                                Maç Merkezi <ArrowRight className="inline ml-2" size={16}/>
                            </Link>
                            <Link to="/oyuncular" className="px-8 py-4 font-bold uppercase tracking-widest border-2 transition-all hover:scale-105" style={{ borderColor: primary, color: primary }}>
                                Kadromuz
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Match Bar */}
                {data.next && (
                    <section className="py-12 border-y-4" style={{ borderColor: primary, backgroundColor: "#0a0a0a" }}>
                        <div className="container-x text-center">
                            <div className="overline mb-3" style={{ color: primary }}>BİR SONRAKİ MAÇIMIZ</div>
                            <div className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                                {data.next.home_team} vs {data.next.away_team}
                            </div>
                            <div className="flex items-center justify-center gap-6 text-neutral-400">
                                <span className="flex items-center gap-2"><Calendar size={14}/>{(data.next.match_date || "").slice(0, 10)}</span>
                                <span className="flex items-center gap-2"><MapPin size={14}/>{data.next.venue || "—"}</span>
                            </div>
                        </div>
                    </section>
                )}

                {/* Posts grid — large cards */}
                <section className="py-20 container-x">
                    <h2 className="text-5xl font-extrabold uppercase mb-10" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Son Haberler</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {data.posts.map((p) => (
                            <Link key={p.id} to={`/haberler/${p.slug}`} className="group relative overflow-hidden aspect-[16/10] block">
                                {p.cover_image && <img src={p.cover_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105" />}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <div className="overline mb-2" style={{ color: primary }}>{p.category}</div>
                                    <h3 className="text-2xl font-bold">{p.title}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Players large strip */}
                <section className="py-20 bg-[#0a0a0a]">
                    <div className="container-x">
                        <h2 className="text-5xl font-extrabold uppercase mb-10" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Yıldızlarımız</h2>
                        <div className="flex gap-4 overflow-x-auto pb-4">
                            {data.players.slice(0, 12).map((p) => (
                                <Link key={p.id} to={`/oyuncular/${p.slug}`} className="flex-shrink-0 w-48 group">
                                    <div className="aspect-[3/4] bg-[#1a1a1a] overflow-hidden mb-2 relative">
                                        {p.photo_url && <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />}
                                        <div className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center font-extrabold" style={{ background: primary, color: "#000" }}>{p.jersey_number || "?"}</div>
                                    </div>
                                    <div className="font-bold uppercase text-sm">{p.name}</div>
                                    <div className="text-xs text-neutral-500">{p.position}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </PublicLayout>
    );
}
